<template>
  <div id="veteran-info" class="form-section">
    <!-- Rank Input Field -->
    <div :key="`rank${disableEligibilityFields ? '-disable' : ''}`">
      <template v-if="disableEligibilityFields">
        <FormField id="rank" labelText="Rank" :required="false">
          <p class="disabled-input">{{ returnWhiteSpaceIfEmptyStr(formResponse.rank) }}</p>
        </FormField>
      </template>
      <template v-else>
        <FormField
          id="rank"
          labelText="Rank"
          placeholder="e.g. Captain"
          type="text"
          :required="false"
          v-model="v$.formResponse.rank.$model"
          :class="validationStatus(v$.formResponse.rank)"
          :error="v$.formResponse.rank.$error"
          :errorMessage="rankError"
        />
      </template>
    </div>

    <!-- Service sector option -->
    <FormField
      :labelText="serviceAgencyLabel"
      id="service-agency"
      :error="v$.formResponse.serviceAgency.$error && v$.formResponse.serviceAgency.$dirty"
      :errorMessage="'This information is required. Please make a selection.'"
    >
      <ul class="radio-group large">
        <li v-for="option in referenceData.serviceAgencyOptions" :key="option.display">
          <input
            type="radio"
            :id="option.returnValue"
            :value="option.display"
            name="serviceAgencyType"
            v-model="v$.formResponse.serviceAgency.$model"
            :disabled="disableEligibilityFields"
          />
          <label :for="option.returnValue">{{ option.display }}</label>
        </li>
      </ul>
    </FormField>

    <!-- Service Type option -->
    <FormField
      :labelText="serviceTypeLabel"
      id="service-type"
      :error="v$.formResponse.serviceType.$error && v$.formResponse.serviceType.$dirty"
      :errorMessage="'This information is required. Please make a selection.'"
    >
      <ul class="radio-group large">
        <li v-for="option in referenceData.serviceTypeOptions" :key="option.display">
          <input
            type="radio"
            :id="option.returnValue"
            :value="option.display"
            name="serviceType"
            v-model="v$.formResponse.serviceType.$model"
            :disabled="disableEligibilityFields"
          />
          <label :for="option.returnValue">{{ option.display }}</label>
        </li>
      </ul>
    </FormField>
  </div>
</template>

<script>
import { required, maxLength } from '@vuelidate/validators';
import useVuelidate from '@vuelidate/core';
import { JOIN_NOW_NAME_VALIDATOR } from '@general/js/validators/PersonValidator';
import { FormField } from '@/components';
import { returnWhiteSpaceIfEmptyStr } from '@/utils';
import { DEFAULT_VETERAN_INFO } from '@general/js/templates/JoinNow/JoinNowSteps/EligibilityTypes';

export default {
  setup() { return { v$: useVuelidate() }; },
  name: 'VeteranInfo',

  data() {
    return {
      formResponse: {
        rank: '',
        serviceAgency: '',
        serviceType: '',
      },
      rankLabel: 'Rank',
    };
  },

  components: {
    FormField,
  },

  props: {
    subFormData: {
      type: Object,
      required: true,
    },
    referenceData: {
      type: Object,
      required: true,
    },
    serviceAgencyLabel: {
      type: String,
      required: true,
    },
    serviceTypeLabel: {
      type: String,
      required: true,
    },
    noEligibilityChange: {
      type: Boolean,
    },
    disableEligibilityFields: {
      type: Boolean,
    },
    initialData: {
      type: Object,
    },
    serviceConnection: {
      type: String,
    },
    defaultSubFormTemplate: {
      type: Object,
    },
    isChangeCover: {
      type: Boolean,
    },
  },

  validations: {
    formResponse: {
      rank: {
        maxLength: maxLength(50),
        JOIN_NOW_NAME_VALIDATOR,
      },
      serviceAgency: {
        required,
      },
      serviceType: {
        required,
      },
    },
  },

  mounted() {
    this.formResponse = { ...this.subFormData };
  },

  computed: {
    rankError() {
      if (this.v$.formResponse.rank.$error && this.v$.formResponse.rank.JOIN_NOW_NAME_VALIDATOR.$invalid) {
        return "We can't accept that input - please remove numbers, symbols and/or consecutive spaces.";
      }
      if (this.v$.formResponse.rank.$error && this.v$.formResponse.rank.maxLength.$invalid) {
        return 'You have exceeded the maximum amount of characters.';
      }

      return 'Please include a response for Rank.';
    },
  },

  watch: {
    formResponse: {
      handler() {
        if (this.formResponse) {
          this.$emit('updated', { veteranInfo: this.formResponse, isInvalid: this.v$.$invalid || this.v$.$error });
        }
      },
      deep: true,
    },
    // Clears and restores prefill data when service connection changes (Self, Partner)
    serviceConnection(newValue) {
      if (this.isChangeCover && this.noEligibilityChange && this.initialData.serviceConnection !== '') {
        if (newValue !== this.initialData.serviceConnection) {
          this.formResponse = { ...DEFAULT_VETERAN_INFO };
        } else {
          this.formResponse = JSON.parse(JSON.stringify(this.initialData.subFormData.veteranInfo));
        }
      }
    },
  },

  methods: {
    validationStatus(validation) {
      // TODO: find out how to modularise this instead of using it multiple times across child components
      return {
        error: validation.$error,
        dirty: validation.$dirty,
      };
    },

    returnWhiteSpaceIfEmptyStr,
  },
};
</script>
